import { lazy, Suspense, useEffect } from 'react';
import { v2UiEnabled } from '@/components/DmpV2/features';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/redux/store.hook';
import { getAuthSelector, setActiveApplication } from '@/redux/auth/auth.slice';
import useAuthApplications from '@/hooks/useAuthApplications';

import Home from '@/pages/Home';
import ForgotPasswordPage from '@/pages/Auth/ForgotPassword';
import SignInPage from '@/pages/Auth/SignIn';
import SignUpPage from '@/pages/Auth/SignUp';
import ExternalAuthCallback from '@/pages/Auth/ExternalAuthCallback';
import Index from '@/pages/Index';
import Registration from '@/pages/Registration';
import Deal from '@/pages/Deal';
import DealInitiation from '@/pages/Deal/DealInitiation';
import DealDetail from '@/pages/Deal/Detail';
import DetailParticipantsPage from '@/pages/Deal/Detail/Participants';
import DetailFilesPage from '@/pages/Deal/Detail/Files';
import ContractSigningPage from '@/pages/Deal/ContractSigning';
import Event from '@/pages/Event';
import YellowPage from '@/pages/YellowPage';
import YellowPageDetail from '@/pages/YellowPage/Detail';
import Translate from '@/pages/Translate';
import Subscription from '@/pages/Subscription';

import PATH from './path';
import LangWrapper from './LangWrapper';
import Services from '@/pages/Index/Services';
import Pricing from '@/pages/Index/Pricing';
import AboutUs from '@/pages/Index/AboutUs';

import ProfileEdit from '@/pages/Profile/Edit';
import CreditReportEdit from '@/pages/Profile/CreditReport';
import IdentificationEdit from '@/pages/Profile/Identification';
import ECertEdit from '@/pages/Profile/ECert';
import SupplementalInfoEdit from '@/pages/Profile/LEI';
import CompanyInfoEdit from '@/pages/Profile/CompanyInfo';
import PersonalInfoEdit from '@/pages/Profile/PersonalInfo';
import Payment from '@/pages/Payment';
import DocLibrary from '@/pages/DocLibrary';
import BlockchainDocuments from '@/pages/BlockchainDocuments';
import { isBlockchainDocumentStorageEnabled } from '@/utils/blockchainDocuments';
import MeetingRedir from '@/pages/Event/JoinMeeting';
import DealMeeting from '@/pages/Deal/Detail/meeting';
import NotificationDetails from '@/pages/Deal/Details';
import ExecutionEdit from '@/components/DealList/ExecutionEdit';
import ESignature from '@/pages/ESignature';
import SignatureV2WorkflowPage from '@/pages/SignatureV2';
import ExternalRosterPage from '@/pages/SignatureV2/ExternalRoster';
import SignatureV2InvitationPage from '@/pages/SignatureV2/Invitation';
import Cart from '@/pages/Cart';
import Checkout from '@/pages/Cart/Checkout';
import CheckoutSuccess from '@/pages/Cart/Success';
import VideoConferencing from '@/pages/Meeting';
import ChatRoom from '@/pages/Chat';
import ChatStart from '@/pages/Chat/Start';
import NewEvent from '@/pages/Event/NewEvent';
import Notifications from '@/pages/Notifications';
import NotificationDetail from '@/pages/Notifications/Detail';
import UploadBankCard from '@/pages/Deal/eSign/UploadBankCard';
import CustomGetHWSign from '@/pages/Deal/eSign/CustomGetHWSign';
import AccountEdit from '@/pages/Profile/AccountEdit';
import ResetPassword from '@/pages/Profile/ResetPassword';
import JoinMeeting from '@/pages/Event/JoinMeeting';
import UserComments from '@/pages/Profile/UserComments';
import CompanyComments from '@/pages/YellowPage/CompanyComments';
import Features from '@/pages/Index/Features';
import LEI from '@/pages/ETrust/LEI';
import CreditReport from '@/pages/ETrust/CreditReport';
import ECert from '@/pages/ETrust/ECert';
import AddNewMembers from '@/pages/Profile/AddNewMembers';
import MembersEdit from '@/pages/Profile/AddNewMembers/MembersEdit';
import BeconeMember from '@/pages/Auth/BecomeMember';
import CompanyCreditReport from '@/pages/CompanyCreditReport';
import PaymentDetail from '@/pages/Payment/Detail';
import LEISearch from '@/pages/LEISearch';
import GuestSign from '@/pages/GuestSign';
import ContractExamples from '@/pages/ContractExamples';
import FeatureDetail from '@/pages/Index/Features/detail';
import TestMCE from '@/pages/Test';
import GuestCreditReport from '@/pages/Guest/CreditReport';
import CreditReportSuccess from '@/pages/Cart/CreditReportSuccess';
import Blog from '@/pages/Index/Blog';
import BlogDetail from '@/pages/Index/Blog/detail';
import WhatIsCR from '@/pages/WhatIsCR';
import AddUserToSubAccount from '@/pages/AddUserToSubAccount';
import Page404 from '@/pages/404';
import StripeSuccess from '@/pages/Cart/StripeSuccess';
import StripeCancel from '@/pages/Cart/StripeCancel';
import TermsOfServiceUpdate from '@/pages/termsUpdate';
import DocumentsShared from '@/pages/DocumentsShared';
import DocumentsTranslate from '@/pages/DocumentsTranslate';
import Share from '@/pages/Share';
import BridgeSecret from '@/pages/Bridge';
import BridgeSetPassword from '@/pages/Bridge/setPassword';
import MeetingVideo from '@/pages/Event/MeetingVideo';
import GoGlobal from '@/pages/ETrust/GoGlobal';
import GoGlobalIntroduction from '@/pages/Index/GoGlobal';
import FreeLegalLink from '@/pages/Index/FreeLegalLink';
import ApplicationSelect from '@/pages/ApplicationSelect';
import VolunteerRegistration from '@/pages/Pbl/VolunteerRegistration';
import PblRoleDashboard from '@/pages/Pbl/RoleDashboard';
import PblEventCalendar from '@/pages/Pbl/EventCalendar';
import PblVettingDashboard from '@/pages/Pbl/VettingDashboard';
import PblVettingDetail from '@/pages/Pbl/VettingDetail';
import PblLawyerApplicationStatus from '@/pages/Pbl/LawyerApplicationStatus';
import PblLawyerCv from '@/pages/Pbl/LawyerCv';
import PblVettingCvRevisions from '@/pages/Pbl/VettingCvRevisions';
import PblVettingCvRevisionDetail from '@/pages/Pbl/VettingCvRevisions/Detail';
import PasswordlessLoginRedirect from '@/pages/Pbl/PasswordlessLoginRedirect';
import PasswordlessCallback from '@/pages/Pbl/PasswordlessCallback';
import PasswordSetup from '@/pages/Pbl/PasswordSetup';
import PblOcCitizenRegistration from '@/pages/Pbl/OcCitizenRegistration';
import PblOcCitizenList from '@/pages/Pbl/OcCitizens';
import PblOcCitizenDetail from '@/pages/Pbl/OcCitizens/Detail';
import PblCitizenManageCases from '@/pages/Pbl/CitizenManageCases';
import PblCitizenCaseApplication from '@/pages/Pbl/CitizenCaseApplication';
import PblOcManageCases from '@/pages/Pbl/OcManageCases';
import PblOcPendingReviewDetail from '@/pages/Pbl/OcManageCases/Detail';
import PblOcLawyerList from '@/pages/Pbl/OcManageCases/LawyerList';
import PblOcLawyerDirectory from '@/pages/Pbl/OcManageCases/LawyerDirectory';
import PblOcManageCaseDetail from '@/pages/Pbl/OcManageCases/ManageCaseDetail';
import PblOcPendingReviewList from '@/pages/Pbl/OcManageCases/PendingReviewList';
import PblLawyerManageCases from '@/pages/Pbl/LawyerManageCases';
import PblLawyerPendingReviewList from '@/pages/Pbl/LawyerManageCases/PendingReviewList';
import PblLawyerPendingReviewDetail from '@/pages/Pbl/LawyerManageCases/Detail';
import PblLawyerMeetingCalendar from '@/pages/Pbl/LawyerMeetingCalendar';
import PblLawyerCaseFeedback from '@/pages/Pbl/LawyerCaseFeedback';
import PblNotifications from '@/pages/Pbl/Notifications';
import PblAppointmentProposalConfirm from '@/pages/Pbl/AppointmentProposalConfirm';
import { FREE_LEGAL_LINK_APPLICATION, findApplication, shouldActivatePblApplication } from '@/utils/applications';
import ProtectRouter from './ProtectRouter';

const AiMedia = lazy(() => import('@/pages/AiMedia'));
const DmpPlanPurchase = lazy(() => import('@/components/DmpV2/PlanPurchasePage'));
const DmpIdentity = lazy(() => import('@/components/DmpV2/IdentityPage'));

const DmpBarristerProfile = lazy(() => import('@/components/DmpV2/BarristerProfilePage'));

const DmpV2Route = ({ children }: { children: React.ReactNode }) =>
  v2UiEnabled() ? (
    <Suspense fallback={<p role="status">…</p>}>{children}</Suspense>
  ) : (
    <Navigate to="/profile/subscriptionDetail" replace />
  );

type AuthState = {
  isLogin: boolean;
  accessToken?: string;
  refreshToken?: string;
  refreshTokenTime?: number;
  userData: any;
  applications?: UserApplicationContext;
  activeApplication?: ApplicationKey | null;
};

interface RoutersProps {
  children: React.ReactNode;
}

const ScrollToTop = () => {
  const { pathname, search } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname, search]);

  return null;
};

const Routers = ({ children }: RoutersProps) => {
  const auth = useAppSelector(getAuthSelector);
  const dispatch = useAppDispatch();

  useAuthApplications();

  const PblRouteGuard = ({ children }: { children: React.ReactElement }) => {
    const pblApplication = findApplication(auth.applications, FREE_LEGAL_LINK_APPLICATION);
    const activatePblApplication = shouldActivatePblApplication({
      isLogin: auth.isLogin,
      activeApplication: auth.activeApplication,
      applications: auth.applications,
    });

    useEffect(() => {
      if (activatePblApplication) {
        dispatch(setActiveApplication(FREE_LEGAL_LINK_APPLICATION));
      }
    }, [activatePblApplication, dispatch]);

    if (!auth.isLogin) {
      return <Navigate to="/auth/signin" replace={true} state={{ redir: window.location.pathname }} />;
    }

    if (!auth.applications) {
      return children;
    }

    if (!pblApplication) {
      return <Navigate to={PATH.application.select} replace />;
    }

    return children;
  };

  return (
    <BrowserRouter>
      <ScrollToTop />
      {children}
      <Routes>
        {/* 靜態資源例外處理 */}
        <Route
          path="/files/*"
          element={<Navigate to={window.location.pathname + window.location.search} replace={true} />}
        />
        <Route path="/" element={<Index />} />
        <Route path={PATH.auth.signIn} element={<SignInPage />} />
        <Route path={PATH.auth.wechatCallback} element={<ExternalAuthCallback />} />
        <Route path={PATH.auth.externalCallback} element={<ExternalAuthCallback />} />
        <Route path={PATH.auth.forgotPassword} element={<ForgotPasswordPage />} />
        <Route path={PATH.auth.signUp} element={<SignUpPage />} />
        <Route path={PATH.application.select} element={<ApplicationSelect />} />
        <Route path={PATH.auth.becomeMember} element={<BeconeMember />} />
        <Route path={PATH.bridge.index} element={<BridgeSecret />} />
        <Route path={PATH.home.index} element={<Home />} />
        <Route path={PATH.home.notifications} element={<Notifications />} />
        <Route path={PATH.home.notificationDetail} element={<NotificationDetail />} />
        <Route path={PATH.profile.edit} element={<ProfileEdit />} />
        <Route path={PATH.profile.accountEdit} element={<AccountEdit />} />
        <Route path={PATH.profile.companyinfo} element={<CompanyInfoEdit />} />
        <Route path={PATH.profile.personalinfo} element={<PersonalInfoEdit />} />
        <Route path={PATH.profile.creditReport} element={<CreditReportEdit />} />
        <Route path={PATH.profile.eCert} element={<ECertEdit />} />
        <Route path={PATH.profile.supplementalInfo} element={<SupplementalInfoEdit />} />
        <Route path={PATH.profile.identificationCheck} element={<IdentificationEdit />} />
        <Route path={PATH.profile.resetPassword} element={<ResetPassword />} />
        <Route path={PATH.profile.comments} element={<UserComments />} />
        <Route path={PATH.profile.addNewMembers} element={<AddNewMembers />} />
        <Route path={PATH.profile.editMembers} element={<MembersEdit />} />
        <Route path={PATH.deal.index} element={<Deal />} />
        <Route path={PATH.deal.notificationDetails} element={<NotificationDetails />} />
        {/* <Route path={PATH.deal.executionEdit} element={<ExecutionEdit />} /> */}
        <Route path={PATH.deal.initiation} element={<DealInitiation />} />
        <Route path={PATH.deal.detail.index} element={<DealDetail />} />
        <Route path={PATH.deal.detail.manageParticipants} element={<DetailParticipantsPage />} />
        <Route path={PATH.deal.detail.manageFiles} element={<DetailFilesPage />} />
        <Route path={PATH.deal.detail.contractSigning} element={<ContractSigningPage />} />
        <Route path={PATH.deal.detail.createMeeting} element={<DealMeeting />} />
        <Route path={PATH.eSign.uploadBankCard} element={<UploadBankCard />} />
        <Route path={PATH.eSign.customGetHWSign} element={<CustomGetHWSign />} />
        <Route path={PATH.event.index} element={<Event />} />
        <Route path={PATH.event.newEvent} element={<NewEvent />} />
        <Route path={PATH.event.joinMeeting} element={<JoinMeeting />} />
        <Route path={PATH.event.meetingVideo} element={<MeetingVideo />} />
        <Route path={PATH.meetingRedirect} element={<MeetingRedir />} />
        <Route path={PATH.yellowPage.index} element={<YellowPage type={'YellowPage'} />} />
        {/* <Route path={PATH.yellowPage.detail} element={<YellowPageDetail />} /> */}
        <Route path={PATH.lawFirmsList.index} element={<YellowPage type={'LawFirmsList'} />} />
        {/* <Route path={PATH.lawFirmsList.detail} element={<YellowPageDetail />} /> */}
        <Route path={PATH.comapnyDetail.index} element={<YellowPageDetail />} />
        <Route path={PATH.guestGoGlobal.introduction} element={<GoGlobalIntroduction isInsidePage={true} />} />
        <Route path={PATH.comapnyDetail.comments} element={<CompanyComments />} />
        <Route path={PATH.translate} element={<Translate />} />
        <Route path={PATH.profile.subscriptionDetail} element={<Subscription />} />
        <Route
          path={PATH.profile.planPurchase}
          element={
            <ProtectRouter auth={auth}>
              <DmpV2Route>
                <DmpPlanPurchase />
              </DmpV2Route>
            </ProtectRouter>
          }
        />
        <Route
          path={PATH.profile.barrister}
          element={
            <ProtectRouter auth={auth}>
              <DmpV2Route>
                <DmpBarristerProfile />
              </DmpV2Route>
            </ProtectRouter>
          }
        />
        <Route
          path={PATH.profile.identity}
          element={
            <ProtectRouter auth={auth}>
              <DmpV2Route>
                <DmpIdentity />
              </DmpV2Route>
            </ProtectRouter>
          }
        />
        <Route path={PATH.registration} element={<Registration />} />
        <Route path={PATH.lei} element={<LEI />} />
        <Route path={PATH.creditReport} element={<CreditReport />} />
        <Route path={PATH.eCert} element={<ECert />} />
        <Route
          path={PATH.goGlobal.index}
          element={
            <ProtectRouter auth={auth}>
              <GoGlobal />
            </ProtectRouter>
          }
        />
        <Route path={PATH.payment.index} element={<Payment />} />
        <Route path={PATH.payment.detail} element={<PaymentDetail />} />
        <Route path={PATH.docLibrary} element={<DocLibrary />} />
        <Route
          path={PATH.blockchainDocuments}
          element={
            <ProtectRouter auth={auth}>
              {isBlockchainDocumentStorageEnabled() ? (
                <BlockchainDocuments />
              ) : (
                <Navigate to={PATH.docLibrary} replace />
              )}
            </ProtectRouter>
          }
        />
        <Route path={PATH.documentsShared} element={<DocumentsShared />} />
        <Route path={PATH.documentsTranslate} element={<DocumentsTranslate />} />
        <Route path={PATH.aiMedia} element={<ProtectRouter auth={auth}><DmpV2Route><AiMedia /></DmpV2Route></ProtectRouter>} />
        <Route path={PATH.share} element={<Share />} />
        <Route path={PATH.eSignature} element={<ESignature />} />
        <Route
          path={PATH.signatureV2.workflow}
          element={
            <ProtectRouter auth={auth}>
              <SignatureV2WorkflowPage />
            </ProtectRouter>
          }
        />
        <Route path={PATH.signatureV2.rosterInvitation} element={<ExternalRosterPage />} />
        <Route path={PATH.signatureV2.invitation} element={<SignatureV2InvitationPage />} />
        <Route path={PATH.meeting} element={<VideoConferencing />} />
        <Route path={PATH.contractExpamples} element={<ProtectRouter auth={auth}><ContractExamples /></ProtectRouter>} />
        <Route
          path={PATH.cart.index}
          element={
            <ProtectRouter auth={auth}>
              <Cart />
            </ProtectRouter>
          }
        />
        <Route
          path={PATH.cart.checkout}
          element={
            <ProtectRouter auth={auth}>
              <Checkout />
            </ProtectRouter>
          }
        />
        <Route path={PATH.cart.checkoutSuccess} element={<CheckoutSuccess type={'success'} />} />
        <Route path={PATH.cart.cancel} element={<CheckoutSuccess type={'cancel'} />} />
        <Route path={PATH.cart.stripeSuccess} element={<StripeSuccess />} />
        <Route path={PATH.cart.stripeCancel} element={<StripeCancel />} />
        <Route
          path={PATH.chatStart}
          element={
            <ProtectRouter auth={auth}>
              <ChatStart />
            </ProtectRouter>
          }
        />
        <Route
          path={PATH.chatRoom}
          element={
            <ProtectRouter auth={auth}>
              <ChatRoom />
            </ProtectRouter>
          }
        />
        <Route path={PATH.companyCreditReport} element={<CompanyCreditReport />} />
        <Route path={PATH.LEISearch} element={<LEISearch />} />
        <Route path={PATH.GuestSign.success} element={<GuestSign type={'success'} />} />
        <Route path={PATH.GuestSign.fail} element={<GuestSign type={'failed'} />} />
        {/* <Route path={PATH.testMCE} element={<TestMCE />} /> */}
        <Route path={PATH.guest.creditReport} element={<GuestCreditReport />} />
        <Route path={PATH.cart.defaultCheckoutCreditReport} element={<CreditReportSuccess />} />
        <Route path={PATH.termsUpdate} element={<TermsOfServiceUpdate />} />
        <Route
          path={PATH.pbl.dashboard}
          element={
            <PblRouteGuard>
              <PblRoleDashboard />
            </PblRouteGuard>
          }
        />
        <Route
          path={PATH.pbl.calendar}
          element={
            <PblRouteGuard>
              <PblEventCalendar />
            </PblRouteGuard>
          }
        />
        <Route
          path={PATH.pbl.lawyerApplicationStatus}
          element={
            <PblRouteGuard>
              <PblLawyerApplicationStatus />
            </PblRouteGuard>
          }
        />
        <Route
          path={PATH.pbl.lawyerCv}
          element={
            <PblRouteGuard>
              <PblLawyerCv />
            </PblRouteGuard>
          }
        />
        <Route
          path={PATH.pbl.vettingCvRevisions}
          element={
            <PblRouteGuard>
              <PblVettingCvRevisions />
            </PblRouteGuard>
          }
        />
        <Route
          path={PATH.pbl.vettingCvRevisionDetail}
          element={
            <PblRouteGuard>
              <PblVettingCvRevisionDetail />
            </PblRouteGuard>
          }
        />
        <Route path={PATH.pbl.passwordlessLogin} element={<PasswordlessLoginRedirect />} />
        <Route path={PATH.pbl.passwordlessCallback} element={<PasswordlessCallback />} />
        <Route path={PATH.pbl.passwordSetup} element={<PasswordSetup />} />
        <Route
          path={PATH.pbl.ocCitizenRegistration}
          element={
            <PblRouteGuard>
              <PblOcCitizenRegistration />
            </PblRouteGuard>
          }
        />
        <Route
          path={PATH.pbl.ocCitizens}
          element={
            <PblRouteGuard>
              <PblOcCitizenList />
            </PblRouteGuard>
          }
        />
        <Route
          path={PATH.pbl.ocCitizenDetail}
          element={
            <PblRouteGuard>
              <PblOcCitizenDetail />
            </PblRouteGuard>
          }
        />
        <Route
          path={PATH.pbl.citizenCases}
          element={
            <PblRouteGuard>
              <PblCitizenManageCases />
            </PblRouteGuard>
          }
        />
        <Route
          path={PATH.pbl.citizenCaseCreate}
          element={
            <PblRouteGuard>
              <PblCitizenCaseApplication />
            </PblRouteGuard>
          }
        />
        <Route
          path={PATH.pbl.citizenCaseEdit}
          element={
            <PblRouteGuard>
              <PblCitizenCaseApplication />
            </PblRouteGuard>
          }
        />
        <Route
          path={PATH.pbl.cases}
          element={
            <PblRouteGuard>
              <PblOcManageCases />
            </PblRouteGuard>
          }
        />
        <Route
          path={PATH.pbl.pendingReviewCases}
          element={
            <PblRouteGuard>
              <PblOcPendingReviewList />
            </PblRouteGuard>
          }
        />
        <Route
          path={PATH.pbl.pendingReviewDetail}
          element={
            <PblRouteGuard>
              <PblOcPendingReviewDetail />
            </PblRouteGuard>
          }
        />
        <Route
          path={PATH.pbl.caseLawyers}
          element={
            <PblRouteGuard>
              <PblOcLawyerList />
            </PblRouteGuard>
          }
        />
        <Route
          path={PATH.pbl.ocLawyerDirectory}
          element={
            <PblRouteGuard>
              <PblOcLawyerDirectory />
            </PblRouteGuard>
          }
        />
        <Route
          path={PATH.pbl.ocAppointmentConfirmation}
          element={
            <PblRouteGuard>
              <PblAppointmentProposalConfirm onBehalf />
            </PblRouteGuard>
          }
        />
        <Route
          path={PATH.pbl.appointmentConfirmation}
          element={
            <PblRouteGuard>
              <PblAppointmentProposalConfirm />
            </PblRouteGuard>
          }
        />
        <Route
          path={PATH.pbl.caseDetail}
          element={
            <PblRouteGuard>
              <PblOcManageCaseDetail />
            </PblRouteGuard>
          }
        />
        <Route
          path={PATH.pbl.notifications}
          element={
            <PblRouteGuard>
              <PblNotifications />
            </PblRouteGuard>
          }
        />
        <Route
          path={PATH.pbl.lawyerCases}
          element={
            <PblRouteGuard>
              <PblLawyerManageCases />
            </PblRouteGuard>
          }
        />
        <Route
          path={PATH.pbl.lawyerMeetingCalendar}
          element={
            <PblRouteGuard>
              <PblLawyerMeetingCalendar />
            </PblRouteGuard>
          }
        />
        <Route
          path={PATH.pbl.lawyerCaseFeedback}
          element={
            <PblRouteGuard>
              <PblLawyerCaseFeedback />
            </PblRouteGuard>
          }
        />
        <Route
          path={PATH.pbl.lawyerPendingReviewCases}
          element={
            <PblRouteGuard>
              <PblLawyerPendingReviewList />
            </PblRouteGuard>
          }
        />
        <Route
          path={PATH.pbl.lawyerPendingReviewDetail}
          element={
            <PblRouteGuard>
              <PblLawyerPendingReviewDetail />
            </PblRouteGuard>
          }
        />
        <Route
          path={PATH.pbl.vettingApplications}
          element={
            <PblRouteGuard>
              <PblVettingDashboard />
            </PblRouteGuard>
          }
        />
        <Route
          path={PATH.pbl.vettingDetail}
          element={
            <PblRouteGuard>
              <PblVettingDetail />
            </PblRouteGuard>
          }
        />

        {/* email信件將既有帳號轉成子帳號 */}
        <Route path={PATH.addUserToSubAccount} element={<AddUserToSubAccount />} />

        {/* 會透過重新導向進入的頁面, 以防舊版本導向沒帶上語系網址, 將以en預設為主 */}
        <Route path={PATH.guest.defaultGetCreditReport} element={<CreditReportSuccess />} />
        {/* 僅訪客能夠訪問的頁面需要加上語系在網址中, 以便SEO搜尋 */}
        <Route path=":lang" element={<LangWrapper />}>
          <Route index element={<Index />} />
          <Route path={PATH.services} element={<Services />} />
          <Route path={PATH.subscription} element={<Pricing />} />
          <Route path={PATH.about} element={<AboutUs />} />
          <Route path={PATH.features.list} element={<Features />} />
          <Route path={PATH.features.detail} element={<FeatureDetail />} />
          <Route path={PATH.guestYellowPage.index} element={<YellowPage type={'YellowPage'} />} />
          <Route path={PATH.guestLawFirmsList.index} element={<YellowPage type={'LawFirmsList'} />} />
          <Route path={PATH.guestComapnyDetail.index} element={<YellowPageDetail />} />
          <Route path={PATH.guest.getCreditReport} element={<CreditReportSuccess />} />
          <Route path={PATH.guest.creditReport} element={<GuestCreditReport />} />
          <Route path={PATH.cart.checkoutCreditReport} element={<CreditReportSuccess />} />
          <Route path={PATH.blog.list} element={<Blog />} />
          <Route path={PATH.blog.detail} element={<BlogDetail />} />
          <Route path={PATH.goGlobal.introduction} element={<GoGlobalIntroduction isInsidePage={false} />} />
          <Route path={PATH.freeLegalLink} element={<FreeLegalLink />} />
          <Route path={PATH.whatIsCR} element={<WhatIsCR />} />
          <Route path={PATH.bridge.setPassword} element={<BridgeSetPassword />} />
          <Route path={PATH.pbl.volunteerRegistration} element={<VolunteerRegistration />} />
          <Route path="*" element={<Page404 />} />
        </Route>
        <Route path="*" element={<Page404 />} />
      </Routes>
    </BrowserRouter>
  );
};

export default Routers;
